import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { FeedbackItem, AIReviewStatus, AIReviewFeedback, hasFeedbackDetails } from "../common/models/types";

type Rating = 1 | 2 | 3 | 4 | 5;

// Backend AIReviewFeedback과 정렬된 상태 구조
interface FeedbackState {
  // 서버 데이터와 동기화
  id: number | null; // feedbackId (null = 미제출)
  aiReviewId: number | null; // 현재 리뷰 ID
  rating: Rating | null; // 별점
  likes: FeedbackItem[] | null; // 유용했던 항목
  dislikes: FeedbackItem[] | null; // 아쉬운 항목
  comment: string | null; // 추가 의견

  // UI 상태
  isSubmitting: boolean;
}

interface FeedbackActions {
  submitRating: (rating: Rating) => Promise<void>;
  setDetails: (data: {
    likes?: FeedbackItem[];
    dislikes?: FeedbackItem[];
    comment?: string;
  }) => Promise<void>;
}

// Phase만으로 UI 상태 결정
type FeedbackPhase = "hidden" | "rating" | "detail" | "thankYou" | "adminView";

interface FeedbackContextValue {
  state: FeedbackState;
  actions: FeedbackActions;
  phase: FeedbackPhase;
}

const initialState: FeedbackState = {
  id: null,
  aiReviewId: null,
  rating: null,
  likes: null,
  dislikes: null,
  comment: null,
  isSubmitting: false,
};

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

// review 객체에서 필요한 정보만 추출
interface ReviewForFeedback {
  id: number;
  feedbackId?: number | null;
  status?: AIReviewStatus;
}

interface FeedbackProviderProps {
  children: React.ReactNode;
  review: ReviewForFeedback;
  // 새로고침 시 기존 피드백 복원용. feedbackId가 있을 때만 fetch 후 전달
  existingFeedback?: AIReviewFeedback | null;
  onSubmitRating: (
    reviewId: number,
    rating: Rating
  ) => Promise<{ feedbackId?: number }>;
  onAddDetails: (
    reviewId: number,
    data: {
      likes?: FeedbackItem[];
      dislikes?: FeedbackItem[];
      comment?: string;
    }
  ) => Promise<void>;
}

export const FeedbackProvider: React.FC<FeedbackProviderProps> = ({
  children,
  review,
  existingFeedback,
  onSubmitRating,
  onAddDetails,
}) => {
  const [state, setState] = useState<FeedbackState>(initialState);
  const [phase, setPhase] = useState<FeedbackPhase>("hidden");

  // useEffect 1: reviewId 변경 시에만 상태 초기화
  useEffect(() => {
    setState({ ...initialState, aiReviewId: review.id });
    setPhase("hidden");
  }, [review.id]);

  // useEffect 2: existingFeedback 도착 시 state 복원 (별점 뱃지 표시용)
  useEffect(() => {
    if (existingFeedback && existingFeedback.aiReviewId === review.id) {
      const rating = existingFeedback.rating;
      setState((prev) => ({
        ...prev,
        id: existingFeedback.id,
        rating: (rating >= 1 && rating <= 5 ? rating : null) as Rating | null,
        likes: existingFeedback.likes,
        dislikes: existingFeedback.dislikes,
        comment: existingFeedback.comment,
      }));
    }
  }, [existingFeedback, review.id]);

  // useEffect 3: feedbackId, status, existingFeedback 변경 시 phase 결정
  useEffect(() => {
    const feedbackId = review.feedbackId;
    const status = review.status;

    if (feedbackId === undefined) {
      setPhase("hidden"); // 로딩 중
    } else if (typeof feedbackId === "number") {
      // tri-state: undefined(fetch 진행 중) / null(실패) / 객체(성공)
      // undefined일 때만 hidden - 별점 입력 fallback 노출 → 재제출 위험 방지
      // null(실패)이면 detail로 진입해 약사 상세 입력 경로 보존 (PATCH는 재시도 가능)
      if (existingFeedback === undefined) {
        setPhase("hidden");
      } else if (existingFeedback && hasFeedbackDetails(existingFeedback)) {
        setPhase("hidden"); // 상세까지 완료된 경우
      } else {
        setPhase((prev) => (prev === "thankYou" ? prev : "detail"));
      }
    } else if (status !== undefined && status !== AIReviewStatus.COMPLETED) {
      setPhase("hidden"); // 리뷰가 완료되지 않음
    } else {
      // feedbackId === null && status === COMPLETED
      // detail/thankYou 상태면 유지, 아니면 rating으로
      setPhase((prev) =>
        prev === "detail" || prev === "thankYou" ? prev : "rating"
      );
    }
  }, [review.feedbackId, review.status, existingFeedback]);

  const submitRating = useCallback(
    async (rating: Rating) => {
      if (!state.aiReviewId || state.isSubmitting) return;

      setState((prev) => ({ ...prev, isSubmitting: true }));
      try {
        const { feedbackId } = await onSubmitRating(state.aiReviewId, rating);
        setState((prev) => ({
          ...prev,
          id: feedbackId ?? null,
          rating,
          isSubmitting: false,
        }));
        setPhase("detail");
      } catch (error) {
        setState((prev) => ({ ...prev, isSubmitting: false }));
        throw error;
      }
    },
    [state.aiReviewId, state.isSubmitting, onSubmitRating]
  );

  const setDetails = useCallback(
    async (data: {
      likes?: FeedbackItem[];
      dislikes?: FeedbackItem[];
      comment?: string;
    }) => {
      if (!state.aiReviewId || state.isSubmitting) return;

      setState((prev) => ({ ...prev, isSubmitting: true }));
      try {
        await onAddDetails(state.aiReviewId, data);
        setState((prev) => ({
          ...prev,
          likes: data.likes || null,
          dislikes: data.dislikes || null,
          comment: data.comment || null,
          isSubmitting: false,
        }));
        setPhase("thankYou");
      } catch (error) {
        setState((prev) => ({ ...prev, isSubmitting: false }));
        throw error;
      }
    },
    [state.aiReviewId, state.isSubmitting, onAddDetails]
  );

  return (
    <FeedbackContext.Provider
      value={{ state, actions: { submitRating, setDetails }, phase }}
    >
      {children}
    </FeedbackContext.Provider>
  );
};

export const useFeedback = (): FeedbackContextValue => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error("useFeedback must be used within FeedbackProvider");
  }
  return context;
};

// Optional hook: Provider 외부에서 안전하게 사용
export const useFeedbackOptional = (): FeedbackContextValue | null => {
  return useContext(FeedbackContext);
};

// Admin 전용 Provider: 읽기 전용 피드백 표시
interface AdminFeedbackProviderProps {
  children: React.ReactNode;
  feedback: AIReviewFeedback;
}

export const AdminFeedbackProvider: React.FC<AdminFeedbackProviderProps> = ({
  children,
  feedback,
}) => {
  const state: FeedbackState = {
    id: feedback.id,
    aiReviewId: feedback.aiReviewId,
    rating: (feedback.rating >= 1 && feedback.rating <= 5 ? feedback.rating : null) as Rating | null,
    likes: feedback.likes,
    dislikes: feedback.dislikes,
    comment: feedback.comment,
    isSubmitting: false,
  };

  // Admin은 액션 불가 (no-op)
  const actions: FeedbackActions = {
    submitRating: async () => {},
    setDetails: async () => {},
  };

  return (
    <FeedbackContext.Provider value={{ state, actions, phase: "adminView" }}>
      {children}
    </FeedbackContext.Provider>
  );
};

export type { Rating, FeedbackPhase, FeedbackState, FeedbackActions };
